// Links in Write (design-docs/Links — design.md §3; the Mac's LinkViews.swift): Add Link… / Edit Link… (⌥⌘L, the format
// bar, the text's menu), the card under the page that shows where the link at the caret goes, and opening a link — a
// web or mail address in a new tab (never with this page as its opener), a document in this project (`fragment:<id>`)
// in the app.
import { useEffect, useMemo, useRef, useState } from 'react';
import type { Project, TreeNode } from '@writing-suite/core';
import { Icon } from './Icons.tsx';
import { currentHost } from '../host/host.ts';

const FRAGMENT = 'fragment:';

/** The document a `fragment:` link points at, else null. */
export function linkedFragment(address: string): string | null {
  return address.toLowerCase().startsWith(FRAGMENT) ? address.slice(FRAGMENT.length) : null;
}

/** A document a link can point to: its fragment, title and the arrangement it's in. */
export interface LinkableDocument { id: string; title: string; place: string }

/** The documents a link can point to, in reading order (`linkableDocuments`): every arrangement but the Trash, in the
 *  project's order (the manuscript first), folders included, each document once. */
export function linkableDocuments(project: Project): LinkableDocument[] {
  const seen = new Set<string>();
  const out: LinkableDocument[] = [];
  const walk = (nodes: TreeNode[], place: string) => {
    for (const n of nodes) {
      const id = n.fragmentId;
      const f = id ? project.fragment(id) : null;
      if (id && f && !f.trashed && !seen.has(id)) {
        seen.add(id);
        out.push({ id, title: f.title || 'Untitled', place });
      }
      walk(n.children, place);
    }
  };
  for (const a of project.arrangements()) if (a.role !== 'trash') walk(project.tree(a.id), a.name);
  return out;
}

/** What a link to a document shows (`linkedDocumentName`): its title, or why it can't be followed; null for any other
 *  link. */
export function linkedDocumentName(project: Project, address: string): string | null {
  const id = linkedFragment(address);
  if (id === null) return null;
  const f = project.fragment(id);
  if (!f) return 'A document that isn’t in this project';
  const title = f.title || 'Untitled';
  return f.trashed ? `“${title}”, in the Trash` : `“${title}”`;
}

/** An address as the writer reads it: encoded spaces and the like decoded, a mail link without `mailto:`. */
export function readableAddress(address: string): string {
  let text: string;
  try {
    text = decodeURIComponent(address);
  } catch {
    text = address;
  }
  return text.toLowerCase().startsWith('mailto:') ? text.slice(7) : text;
}

/** Only web and mail addresses are opened by the browser (a link's address always has a known scheme; this keeps it so). */
export const isWebAddress = (address: string) => /^(https?:|mailto:)/i.test(address);

/** Opens a link: a document in this project in the app (`openDocument`), else a new tab or the mail app. */
export function openLink(address: string, openDocument: (fragmentId: string) => void) {
  const fragment = linkedFragment(address);
  if (fragment !== null) {
    openDocument(fragment);
    return;
  }
  if (!isWebAddress(address)) return;
  const shell = currentHost();
  if (shell?.has('links.open')) {
    void shell.openOutside(address); // a native shell opens it in the system's browser or mail app (host/host.ts)
    return;
  }
  const a = document.createElement('a');
  a.href = address;
  a.rel = 'noopener noreferrer';
  if (!/^mailto:/i.test(address)) a.target = '_blank';
  a.click();
}

/** Add Link… or Edit Link…: the address for the selected words (or the link the caret is in, or the word at the caret).
 *  `onSave` returns why it couldn't (the dialog stays open), or null when it linked. */
export function LinkDialog({
  existing,
  documents,
  onSave,
  onRemove,
  onCancel,
}: {
  /** The address of the link being edited; null to add one. */
  existing: string | null;
  /** The documents a link can point to (`linkableDocuments`), for A Document Here. */
  documents: LinkableDocument[];
  onSave: (typed: string) => string | null;
  onRemove: () => void;
  onCancel: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const existingDocument = existing !== null ? linkedFragment(existing) : null;
  const [target, setTarget] = useState<'address' | 'document'>(existingDocument !== null ? 'document' : 'address');
  const [address, setAddress] = useState(() => (existing === null || existingDocument !== null ? '' : decodeOrKeep(existing)));
  const [documentId, setDocumentId] = useState<string | null>(existingDocument);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const shown = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    const all = query ? documents.filter((d) => d.title.toLocaleLowerCase().includes(query)) : documents;
    return all.slice(0, 200);
  }, [documents, search]);
  const canSave = target === 'address' ? address.trim() !== '' : documentId !== null;
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
    return () => d?.close();
  }, []);
  const save = () => {
    if (!canSave) return;
    setError(onSave(target === 'document' ? `fragment:${documentId}` : address));
  };
  return (
    <dialog
      ref={ref}
      className="confirm text-dialog link-dialog"
      aria-labelledby="link-dialog-title"
      onCancel={(e) => {
        e.preventDefault();
        onCancel();
      }}
    >
      <form
        method="dialog"
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
      >
        <h2 id="link-dialog-title">{existing === null ? 'Add Link' : 'Edit Link'}</h2>
        <div className="segmented link-target" role="radiogroup" aria-label="Link to">
          {(
            [
              ['address', 'Web or Email Address'],
              ['document', 'A Document Here'],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={target === value}
              className={target === value ? 'on' : undefined}
              onClick={() => {
                setTarget(value);
                setError(null);
              }}
            >
              {label}
            </button>
          ))}
        </div>
        {target === 'document' ? (
          <>
            <input
              type="search"
              className="link-document-search"
              aria-label="Find a document"
              placeholder="Find a document"
              value={search}
              autoFocus
              onChange={(e) => setSearch(e.target.value)}
            />
            <ul className="link-documents" role="listbox" aria-label="Documents">
              {shown.map((d) => (
                <li
                  key={d.id}
                  role="option"
                  aria-selected={documentId === d.id}
                  data-fragment-id={d.id}
                  className={documentId === d.id ? 'selected' : undefined}
                  tabIndex={0}
                  onClick={() => setDocumentId(d.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setDocumentId(d.id);
                    }
                  }}
                >
                  <span className="link-document-text">
                    <span className="link-document-title">{d.title}</span>
                    <span className="link-document-place">{d.place}</span>
                  </span>
                  {documentId === d.id ? <Icon name="check" size={14} className="link-document-check" /> : null}
                </li>
              ))}
              {shown.length === 0 ? <li className="link-documents-empty">No documents match.</li> : null}
            </ul>
            <p className="link-dialog-note">Choosing the link opens the document. In an ebook it goes to that document’s chapter.</p>
          </>
        ) : (
          <>
        <label className="field-label stacked-label" htmlFor="link-dialog-address">
          Address
        </label>
        <input
          id="link-dialog-address"
          type="text"
          inputMode="url"
          autoComplete="off"
          autoCapitalize="off"
          spellCheck={false}
          placeholder="https://…"
          value={address}
          autoFocus
          aria-invalid={error !== null || undefined}
          aria-describedby={error !== null ? 'link-dialog-error' : 'link-dialog-note'}
          onChange={(e) => {
            setAddress(e.target.value);
            setError(null);
          }}
          onFocus={(e) => e.target.select()}
        />
        {error !== null ? (
          <p className="link-dialog-error" id="link-dialog-error" role="alert">
            {error}
          </p>
        ) : (
          <p className="link-dialog-note" id="link-dialog-note">
            A web address, or an email address to write to. In your text the words show as a link; ⌘-click one (Ctrl-click
            off the Mac) to open it.
          </p>
        )}
          </>
        )}
        <div className="actions">
          {existing !== null ? (
            <button type="button" className="button secondary danger link-dialog-remove" onClick={onRemove}>
              Remove Link
            </button>
          ) : null}
          <button type="button" className="button secondary" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="button primary" disabled={!canSave}>
            {existing === null ? 'Add' : 'Done'}
          </button>
        </div>
      </form>
    </dialog>
  );
}

function decodeOrKeep(address: string): string {
  try {
    return decodeURIComponent(address);
  } catch {
    return address;
  }
}

/** Where the link at the caret goes, under the page, with Open, Edit… and Remove. */
export function LinkCard({
  address,
  documentTitle,
  canWrite,
  onOpenDocument,
  onEdit,
  onRemove,
}: {
  address: string;
  /** For a link to a document: what it shows (`linkedDocumentName`). */
  documentTitle: string | null;
  canWrite: boolean;
  onOpenDocument: (fragmentId: string) => void;
  onEdit: () => void;
  onRemove: () => void;
}) {
  const fragment = linkedFragment(address);
  const mail = /^mailto:/i.test(address);
  const shown = fragment !== null ? documentTitle ?? 'A document that isn’t in this project' : readableAddress(address);
  return (
    <section className="link-card" aria-label={`Link to ${shown}`} data-link-address={address}>
      <span className="link-card-glyph" aria-hidden="true">
        <Icon name={fragment !== null ? 'doc' : mail ? 'envelope' : 'link'} size={16} />
      </span>
      <p className="link-card-address" title={fragment !== null ? shown : readableAddress(address)}>
        {shown}
      </p>
      <div className="link-card-actions">
        {fragment !== null ? (
          <button type="button" className="pill outline" onMouseDown={(e) => e.preventDefault()} onClick={() => onOpenDocument(fragment)}>
            Open
          </button>
        ) : isWebAddress(address) ? (
          <a className="pill outline" href={address} target={mail ? undefined : '_blank'} rel="noopener noreferrer" title="Open the link (⌘-click it in the text)">
            Open
          </a>
        ) : null}
        <button type="button" className="pill outline" onMouseDown={(e) => e.preventDefault()} onClick={onEdit} disabled={!canWrite}>
          Edit…
        </button>
        <button type="button" className="pill outline" onMouseDown={(e) => e.preventDefault()} onClick={onRemove} disabled={!canWrite}>
          Remove
        </button>
      </div>
    </section>
  );
}
